import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { CATEGORIES, AGE_GROUPS, DURATIONS, CONDITIONS, EXCHANGE_MODES, type ExchangeMode } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/edit-book/$id")({
  head: () => pageHead({ path: "/edit-book", title: "Edit your listing — Book Swap", description: "Fix a typo, update the condition, or change how you're sharing your book on Book Swap.", noindex: true }),
  component: EditBook,
});

type Form = {
  title: string; author: string; isbn: string; category: string; age_group: string;
  exchange_duration: string; condition: string; description: string;
  exchange_mode: ExchangeMode; status: string;
};

function EditBook() {
  const { id } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => { if (!authLoading && !user) navigate({ to: "/login" }); }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("books").select("*").eq("id", id).maybeSingle();
      if (cancelled) return;
      if (!data || data.user_id !== user.id) { setDenied(true); return; }
      setForm({
        title: data.title, author: data.author, isbn: data.isbn ?? "",
        category: data.category, age_group: data.age_group,
        exchange_duration: data.exchange_duration, condition: data.condition,
        description: data.description ?? "",
        exchange_mode: (data.exchange_mode as ExchangeMode) ?? "swap",
        status: data.status,
      });
    })();
    return () => { cancelled = true; };
  }, [id, user]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    const { error } = await supabase.from("books").update({
      title: form.title,
      author: form.author,
      isbn: form.isbn || null,
      category: form.category,
      age_group: form.age_group,
      exchange_duration: form.exchange_mode === "lend" ? (form.exchange_duration || "1 Week") : "1 Month",
      condition: form.condition,
      description: form.description || null,
      exchange_mode: form.exchange_mode,
      is_donation: form.exchange_mode === "donate",
    }).eq("id", id);
    setSaving(false);
    if (error) { console.error(error); return toast.error("Could not save your changes."); }
    toast.success("Listing updated");
    navigate({ to: "/my-books" });
  }

  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;
  if (denied) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">You can only edit your own listings.</p></div>;
  if (!form) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">Loading…</p></div>;

  const midTransaction = form.status === "unavailable" || form.status === "reserved";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Link to="/my-books" className="text-sm text-primary hover:underline">← Back to my books</Link>
        <h1 className="mt-3 text-3xl font-bold">Edit listing</h1>
        <p className="mt-1 text-sm text-muted-foreground">Fix a typo or change how you're sharing this book.</p>

        {midTransaction && (
          <div className="mt-4 rounded-xl bg-warning/10 px-4 py-3 text-sm text-warning">
            This book is currently part of an active exchange, so the sharing mode is locked until it's back.
          </div>
        )}

        <form onSubmit={save} className="mt-6 space-y-6 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" required maxLength={150} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="author">Author *</Label>
              <Input id="author" required maxLength={100} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
          </div>

          <div>
            <Label htmlFor="isbn">ISBN</Label>
            <Input id="isbn" value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} />
          </div>

          <div className="rounded-xl border border-primary/20 bg-primary-soft/30 p-4">
            <Label className="text-base font-semibold">How you're sharing it</Label>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {EXCHANGE_MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  disabled={midTransaction}
                  onClick={() => setForm({ ...form, exchange_mode: m.value })}
                  aria-pressed={form.exchange_mode === m.value}
                  className={`rounded-xl border p-3 text-start transition disabled:opacity-50 ${
                    form.exchange_mode === m.value
                      ? "border-primary bg-card shadow-[var(--shadow-card)]"
                      : "border-border bg-background hover:border-primary/50"
                  }`}
                >
                  <span className="block text-sm font-semibold">{m.emoji} {m.label}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{m.hint}</span>
                </button>
              ))}
            </div>
            {form.exchange_mode === "lend" && (
              <div className="mt-4">
                <FieldSelect label="Lending duration *" value={form.exchange_duration} onChange={(v) => setForm({ ...form, exchange_duration: v })} options={DURATIONS as any} />
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <FieldSelect label="Category *" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES as any} />
            <FieldSelect label="Age group *" value={form.age_group} onChange={(v) => setForm({ ...form, age_group: v })} options={AGE_GROUPS as any} />
            <FieldSelect label="Condition *" value={form.condition} onChange={(v) => setForm({ ...form, condition: v })} options={CONDITIONS as any} />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" maxLength={500} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <Button type="submit" disabled={saving} className="w-full bg-primary hover:bg-primary/90">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
